import { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/feature/PageHeader";
import SegmentedTabs from "@/components/feature/SegmentedTabs";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { news } from "@/mocks/news";
import { newsCategoryFilters } from "@/mocks/site";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function News() {
  const [category, setCategory] = useState<string | number>("全部");
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [mode, setMode] = useState<"page" | "more">("page");
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useDocumentMeta({
    title: "美国禁忌4最新资讯 - 剧集动态与专题解读",
    description:
      "美国禁忌4最新资讯栏目，汇总剧集新闻、官方动态、深度解析、花絮幕后与人物采访，可按类别与发布时间筛选查看。",
  });

  const filtered = useMemo(() => {
    const list = news.filter((n) => category === "全部" || n.category === category);
    return [...list].sort((a, b) =>
      sort === "desc" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)
    );
  }, [category, sort]);

  useEffect(() => {
    setPage(1);
    setVisible(PAGE_SIZE);
  }, [category, sort, mode]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const moreList = filtered.slice(0, visible);

  const reset = () => {
    setCategory("全部");
    setSort("desc");
  };

  return (
    <div className="px-4 md:px-10 py-6 md:py-10">
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "最新资讯" }]}
        eyebrow="News"
        title="最新资讯"
        description="追踪《美国禁忌4》的剧集动态、官方消息、幕后花絮与深度解读，帮助你从不同角度理解这部作品。"
        meta={`当前展示 ${filtered.length} 条资讯 · 共 ${totalPages} 页`}
      />

      <div className="flex flex-col gap-4 mb-6 md:mb-8">
        <SegmentedTabs
          ariaLabel="按资讯类别筛选"
          options={newsCategoryFilters}
          value={category}
          onChange={setCategory}
        />

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
            <i className="ri-sort-desc text-foreground-500 text-sm" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as "desc" | "asc")}
              aria-label="按日期排序"
              className="bg-transparent text-sm text-foreground-800 focus:outline-none cursor-pointer"
            >
              <option value="desc">按日期 · 最新优先</option>
              <option value="asc">按日期 · 最早优先</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
            {[
              { value: "page", label: "分页浏览", icon: "ri-layout-grid-line" },
              { value: "more", label: "加载更多", icon: "ri-loader-4-line" },
            ].map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMode(m.value as "page" | "more")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-label tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                  mode === m.value
                    ? "bg-accent-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900 hover:bg-background-200/60"
                }`}
              >
                <i className={m.icon} />
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length > 0 ? (
        mode === "page" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
              {paged.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination current={currentPage} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
              {moreList.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <div className="flex justify-center mt-10">
              {visible < filtered.length ? (
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-6 py-2.5 text-sm font-label text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-loader-4-line" />
                  加载更多资讯
                </button>
              ) : (
                <p className="text-xs text-foreground-500 font-label">已经到底啦 · 共 {filtered.length} 条资讯</p>
              )}
            </div>
          </>
        )
      ) : (
        <EmptyState
          title="没有找到匹配的资讯"
          description="当前类别与日期条件下暂无相关资讯，试试切换资讯类别或重置筛选条件。"
          onReset={reset}
        />
      )}
    </div>
  );
}